import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MessageSquare, Trash2, Search, X } from 'lucide-react';
import { chats as chatsApi } from '../utils/api';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';

const ChatsPage = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await chatsApi.getAll();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter conversations by search query (matches title or message content)
  const filteredConversations = conversations.filter(chat => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    // Search in title
    if (chat.title?.toLowerCase().includes(query)) return true;
    // Search in messages content
    if (chat.messages?.some(msg => msg.content?.toLowerCase().includes(query))) return true;
    return false;
  });

  const handleNewChat = async () => {
    // Clear any existing localStorage chat and start fresh
    localStorage.removeItem('newRecipeConversation');
    localStorage.removeItem('currentChatId');
    navigate('/create');
  };

  const handleOpenChat = (chatId) => {
    localStorage.setItem('currentChatId', chatId);
    navigate('/create');
  };

  const handleDeleteChat = async (chatId, e) => {
    e.stopPropagation();
    
    try {
      console.log('[ChatsPage] Deleting chat:', chatId);
      await chatsApi.delete(chatId);
      console.log('[ChatsPage] Chat deleted successfully');
      setConversations(prev => prev.filter(c => c.chat_id !== chatId));
      // Also clear from localStorage if it was the current chat
      if (localStorage.getItem('currentChatId') === chatId) {
        localStorage.removeItem('currentChatId');
      }
    } catch (error) {
      console.error('[ChatsPage] Failed to delete chat:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    
    // Reset times to midnight for accurate day comparison
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const diffMs = today - dateDay;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-800">Chats</h1>
            <button
              onClick={handleNewChat}
              data-testid="new-chat-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <Plus size={20} />
              <span>New Chat</span>
            </button>
          </div>
          
          {/* Search Input */}
          {conversations.length > 0 && (
            <div className="mt-4 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="chat-search-input"
                className="w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  data-testid="clear-search-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
        {loading ? (
          <LoadingSpinner message="Loading chats..." />
        ) : conversations.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No conversations yet</h2>
            <p className="text-gray-500 mb-6">Start a new chat to create recipes with AI</p>
            <button
              onClick={handleNewChat}
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <Plus size={20} />
              Start New Chat
            </button>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-12">
            <Search size={48} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No matching chats</h2>
            <p className="text-gray-500 mb-4">No chats found for &ldquo;{searchQuery}&rdquo;</p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-amber-600 hover:text-amber-700 font-medium"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConversations.map((chat) => (
              <div
                key={chat.chat_id}
                onClick={() => handleOpenChat(chat.chat_id)}
                data-testid={`chat-item-${chat.chat_id}`}
                className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow border border-gray-100 group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-800 truncate">{chat.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {chat.message_count} messages · {formatDate(chat.updated_at)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDeleteChat(chat.chat_id, e)}
                    data-testid={`delete-chat-${chat.chat_id}`}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors sm:opacity-0 sm:group-hover:opacity-100"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default ChatsPage;
